import axios from "axios";
import * as yup from "yup";

export interface BookingData {
  name: string;
  email: string;
  date: string;
  time: string;
  guests: string;
  phone?: string;
  specialRequests?: string;
  newsletter: boolean; // Add newsletter field
}

// Validation schema for booking data
const bookingSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  date: yup.string().required("Date is required"),
  time: yup.string().required("Time is required"),
  guests: yup.string().required("Number of guests is required"),
  newsletter: yup.boolean().optional(), // Add newsletter validation
});

// Get the base URL dynamically
const getBaseUrl = () => {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return process.env.NODE_ENV === "production"
    ? "https://www.waveofflavors.fi"
    : "http://localhost:5173";
};

// Update the API URL to ensure it's correctly pointing to your backend
const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://www.waveofflavors.fi/api/bookings"
    : "http://localhost:3001/api/bookings";

export const submitBooking = async (bookingData: BookingData) => {
  try {
    console.log("Submitting booking to:", API_URL);
    console.log("Booking data:", bookingData);

    // Validate booking data
    await bookingSchema.validate(bookingData);

    // Add a timestamp to avoid caching issues
    const timestamp = new Date().getTime();

    // Add a longer timeout for potential network issues
    const response = await axios.post(
      `${API_URL}?t=${timestamp}`,
      bookingData,
      {
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        withCredentials: true,
        timeout: 60000, // Increase timeout to 60 seconds
      }
    );

    console.log("Booking API response:", response);

    if (response.status !== 200 || !response.data.success) {
      const errorMessage =
        response.data.error ||
        "Failed to submit booking. Please try again later.";
      console.error("Server returned error:", errorMessage);
      throw new Error(errorMessage);
    }

    return response.data;
  } catch (error) {
    console.error("Booking error:", error);

    if (axios.isAxiosError(error)) {
      // Network errors, timeouts, etc.
      if (!error.response) {
        console.error("Network error - no response received");
        throw new Error(
          "Unable to connect to the booking service. Please check your internet connection and try again."
        );
      }

      // Specific error based on status code
      if (error.response.status === 500) {
        console.error("Server error (500):", error.response.data);
        throw new Error(
          "The booking server encountered an error. Our team has been notified. Please try again later."
        );
      }

      // Use server-provided error message when available
      if (error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      }
    }

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("An unexpected error occurred. Please try again later.");
  }
};